/**
 * DPL DEW — Industry Service
 *
 * Fetches industries, project types, scope group definitions, and scope
 * templates for the current tenant. All reads go through the tenant-scoped
 * RLS — no explicit tenant_id filter needed in queries.
 *
 * Responses are cached in memory for the session; call refresh() to bust.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

export type Industry        = Database['public']['Tables']['industries']['Row'];
export type ProjectType     = Database['public']['Tables']['project_types']['Row'];
export type ScopeGroupDef   = Database['public']['Tables']['scope_group_definitions']['Row'];
export type ScopeTemplate   = Database['public']['Tables']['scope_templates']['Row'];
export type SectionTemplate = Database['public']['Tables']['section_templates']['Row'];
export type PaymentScheduleTemplate =
  Database['public']['Tables']['payment_schedule_templates']['Row'];

export type EstimationMode = 'flat' | 'activity' | 'scope_matrix';

/** Full project type with nested template + section data */
export interface ProjectTypeWithTemplate extends ProjectType {
  scope_template: (ScopeTemplate & {
    section_templates: SectionTemplate[];
  }) | null;
  payment_schedule_template: PaymentScheduleTemplate | null;
  scope_group_definitions: ScopeGroupDef[];
}

/** Full industry with its project types */
export interface IndustryWithProjectTypes extends Industry {
  project_types: ProjectType[];
  scope_group_definitions: ScopeGroupDef[];
}

// ── In-memory cache ───────────────────────────────────────────

let _industries:    IndustryWithProjectTypes[] | null = null;
let _projectTypes:  Record<string, ProjectTypeWithTemplate> = {};

// ── Public API ────────────────────────────────────────────────

/**
 * Get all active industries available to this tenant
 * (includes both system rows and tenant-cloned rows)
 */
export async function getIndustries(): Promise<IndustryWithProjectTypes[]> {
  if (_industries) return _industries;

  const { data: industries, error: indErr } = await supabase
    .from('industries')
    .select('*')
    .eq('is_active', true)
    .order('sort_order');

  if (indErr || !industries || industries.length === 0) return [];

  const dedupedIndustries = Array.from(
    industries.reduce((acc, industry) => {
      const current = acc.get(industry.slug);
      if (!current || (!industry.is_system && current.is_system)) {
        acc.set(industry.slug, industry);
      }
      return acc;
    }, new Map<string, Industry>()),
  )
    .map(([, industry]) => industry)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  const industryIds = dedupedIndustries.map((industry) => industry.id);

  const [ptRes, sgdRes] = await Promise.all([
    supabase
      .from('project_types')
      .select('*')
      .in('industry_id', industryIds)
      .eq('is_active', true)
      .order('sort_order'),
    supabase
      .from('scope_group_definitions')
      .select('*')
      .in('industry_id', industryIds)
      .order('sort_order'),
  ]);

  const ptsByIndustry = groupBy(ptRes.data ?? [], 'industry_id');
  const sgdsByIndustry = groupBy(sgdRes.data ?? [], 'industry_id');

  _industries = dedupedIndustries.map(ind => ({
    ...ind,
    project_types:           ptsByIndustry[ind.id]  ?? [],
    scope_group_definitions: sgdsByIndustry[ind.id] ?? [],
  }));

  return _industries;
}

/**
 * Get a single project type with its full template tree.
 * Used when the user selects a project type in the wizard.
 */
export async function getProjectTypeWithTemplate(
  projectTypeId: string,
): Promise<ProjectTypeWithTemplate | null> {
  if (_projectTypes[projectTypeId]) return _projectTypes[projectTypeId];

  // Parallel fetch: project type + scope template + payment template
  const [ptRes, stRes, pstRes] = await Promise.all([
    supabase.from('project_types').select('*').eq('id', projectTypeId).single(),
    supabase
      .from('scope_templates')
      .select('*, section_templates(*)')
      .eq('project_type_id', projectTypeId)
      .eq('is_active', true)
      // Prefer tenant copy over system copy
      .order('is_system', { ascending: true })
      .limit(1)
      .maybeSingle(),
    supabase
      .from('payment_schedule_templates')
      .select('*')
      .eq('project_type_id', projectTypeId)
      // Prefer tenant copy
      .order('tenant_id', { ascending: false, nullsFirst: false })
      .limit(1)
      .maybeSingle(),
  ]);

  if (ptRes.error || !ptRes.data) return null;

  // Load scope group definitions for this project type's industry
  const { data: sgds } = await supabase
    .from('scope_group_definitions')
    .select('*')
    .eq('industry_id', ptRes.data.industry_id)
    .order('sort_order');

  // Sort section templates by sort_order
  const scopeTemplate = stRes.data
    ? {
        ...stRes.data,
        section_templates: [...(stRes.data.section_templates ?? [])].sort(
          (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
        ),
      }
    : null;

  const result: ProjectTypeWithTemplate = {
    ...ptRes.data,
    scope_template:            scopeTemplate,
    payment_schedule_template: pstRes.data ?? null,
    scope_group_definitions:   sgds ?? [],
  };

  _projectTypes[projectTypeId] = result;
  return result;
}

/**
 * Check which project types have no scope template configured.
 * Used to display the "no template" warning in the UI.
 */
export async function getMissingTemplateWarnings(): Promise<
  { project_type_id: string; project_type_name: string; industry_name: string }[]
> {
  const { data, error } = await supabase.rpc('fn_warn_missing_template', {
    p_tenant_id: await getTenantId(),
  });
  if (error) {
    console.warn('[IndustryService] fn_warn_missing_template error:', error.message);
    return [];
  }
  return data ?? [];
}

/**
 * Get catalogue presets for a scope × work type combination.
 * Returns items ranked by match tier: strict → by_scope → by_work_type
 */
export async function getCataloguePresets(
  scopeType:  string | null,
  workType:   string,
  industryId?: string,
) {
  const tenantId = await getTenantId();
  const { data, error } = await supabase.rpc('fn_get_catalogue_presets', {
    p_tenant_id:   tenantId,
    p_scope_type:  scopeType,
    p_work_type:   workType,
    p_industry_id: industryId || null,
  });
  if (error) {
    console.error('[IndustryService] fn_get_catalogue_presets error:', error.message);
    return [];
  }
  return data ?? [];
}

// ── Per-industry engine defaults ─────────────────────────────

export interface IndustryDefaults {
  materials_used: string[];
  inclusions: string[];
  exclusions: string[];
  general_terms: string[];
  warranty: string[];
  default_payment_schedule: { milestoneName: string; percentage: number; dueStage: string }[];
  default_validity_days: number;
  default_gst_rate: number;
}

const _industryDefaults: Record<string, IndustryDefaults> = {};

export async function getIndustryDefaults(industryId: string): Promise<IndustryDefaults | null> {
  if (_industryDefaults[industryId]) return _industryDefaults[industryId];

  const { data, error } = await supabase
    .from('industry_defaults')
    .select('*')
    .eq('industry_id', industryId)
    .maybeSingle();

  if (error || !data) return null;

  const result: IndustryDefaults = {
    materials_used:           (data.materials_used as string[])  ?? [],
    inclusions:               (data.inclusions as string[])      ?? [],
    exclusions:               (data.exclusions as string[])      ?? [],
    general_terms:            (data.general_terms as string[])   ?? [],
    warranty:                 (data.warranty as string[])        ?? [],
    default_payment_schedule: (data.default_payment_schedule as unknown[]) ?? [],
    default_validity_days:    data.default_validity_days ?? 30,
    default_gst_rate:         Number(data.default_gst_rate ?? 18),
  };
  _industryDefaults[industryId] = result;
  return result;
}

export async function saveIndustryDefaults(
  industryId: string,
  defaults: IndustryDefaults,
): Promise<void> {
  const tenantId = await getTenantId();
  const { error } = await supabase
    .from('industry_defaults')
    .upsert(
      { tenant_id: tenantId, industry_id: industryId, ...defaults, updated_at: new Date().toISOString() },
      { onConflict: 'tenant_id,industry_id' },
    );
  if (error) throw error;
  _industryDefaults[industryId] = defaults;
}

/** Bust in-memory cache (call after settings changes) */
export function refreshIndustryCache(): void {
  _industries   = null;
  _projectTypes = {};
  Object.keys(_industryDefaults).forEach(k => delete _industryDefaults[k]);
}

// ── Helpers ───────────────────────────────────────────────────

export class SessionExpiredError extends Error {
  constructor(message = 'Session expired. Please log in again.') {
    super(message);
    this.name = 'SessionExpiredError';
  }
}

async function getTenantId(): Promise<string> {
  let res = await supabase.rpc('current_tenant_id');
  
  if (res.error || !res.data) {
    const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
    if (refreshError || !refreshData.session) {
      throw new SessionExpiredError();
    }
    res = await supabase.rpc('current_tenant_id');
  }
  
  if (!res.data) {
    throw new Error('Not authenticated or no tenant assigned');
  }
  return res.data;
}

function groupBy<T extends Record<string, unknown>>(
  arr: T[],
  key: keyof T,
): Record<string, T[]> {
  return arr.reduce((acc, item) => {
    const k = String(item[key]);
    if (!acc[k]) acc[k] = [];
    acc[k].push(item);
    return acc;
  }, {} as Record<string, T[]>);
}

export const industryService = {
  getIndustries,
  getProjectTypeWithTemplate,
  getMissingTemplateWarnings,
  getCataloguePresets,
  getIndustryDefaults,
  saveIndustryDefaults,
  refreshCache: refreshIndustryCache,
};
